import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const PopupContext = createContext();

// One place for every transient message in the app:
//   showAlert / showConfirm  blocking, answered by the user (rendered as a sheet by GlobalPopup)
//   showToast                non-blocking confirmation ("Feedback sent"), auto-dismissing, announced to screen readers
// Normal interactions never use browser-native alert()/confirm().
export const PopupProvider = ({ children }) => {
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'alert', // 'alert' | 'confirm'
    onConfirm: null,
    onCancel: null,
  });
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  const showAlert = (title, message) => {
    return new Promise((resolve) => {
      setPopup({
        show: true,
        title,
        message,
        type: 'alert',
        onConfirm: () => {
          setPopup((prev) => ({ ...prev, show: false }));
          resolve(true);
        },
      });
    });
  };

  const showConfirm = (title, message) => {
    return new Promise((resolve) => {
      setPopup({
        show: true,
        title,
        message,
        type: 'confirm',
        onConfirm: () => {
          setPopup((prev) => ({ ...prev, show: false }));
          resolve(true);
        },
        onCancel: () => {
          setPopup((prev) => ({ ...prev, show: false }));
          resolve(false);
        },
      });
    });
  };

  const dismissToast = useCallback((id) => setToasts(list => list.filter(t => t.id !== id)), []);
  const showToast = useCallback((message, { tone = 'info', duration = 4000 } = {}) => {
    const id = nextId.current++;
    setToasts(list => [...list.slice(-2), { id, message, tone }]);     // at most 3 on screen
    if (duration > 0) setTimeout(() => dismissToast(id), duration);
    return id;
  }, [dismissToast]);

  const value = useMemo(() => ({ popup, showAlert, showConfirm, toasts, showToast, dismissToast }), [popup, toasts, showToast, dismissToast]);
  return <PopupContext.Provider value={value}>{children}</PopupContext.Provider>;
};

export const usePopup = () => useContext(PopupContext);
